import React, {ReactElement, useState} from 'react'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {Size, Spacing} from 'types/Theme'

import {cssTheme} from '../../../themes'
import {Divider} from '../Divider'
import {Icon, IconName} from '../Icon'

type AccordionSize = Extract<Size, 'sm' | 'md' | 'lg'>
interface AccordionProps {
  /** The content rendered inside the open Accordion */
  children: React.ReactNode
  /** Pass through className to allow styles overrides */
  className?: string
  /** The header or title that is always on display */
  header?: ReactElement | string
  /** Whether the accordion will start collapsed or not */
  isOpenByDefault?: boolean
  /** Callback invoked whenever the collapsed state changes */
  onToggle?: (state: boolean) => void
  /** Icon name from icon library */
  icon?: IconName
  /** If set, accordion trigger will be rendered under the collapsible content */
  isBottomAligned?: boolean
  /** The size of the accordion header */
  size?: AccordionSize
  /** Identify the element for selection in integration tests, FullStory, etc. */
  dataId?: string
  /** Whether to show divider on the bottom of Accordion */
  withDivider?: boolean
  /** Size of header side paddings */
  sidePaddings?: Spacing
  /** Callback on header click. Must not call `preventDefault()` - that blocks the native toggle. */
  onClick?: (e: React.MouseEvent<HTMLElement>) => void
}

const ICON_WRAPPER_HEIGHT = {
  sm: 22,
  md: 24,
  lg: 28
}

const TabElement = styled.div<{sidePaddings: Spacing}>`
  display: flex;
  flex-direction: column;
  padding: 0 ${({sidePaddings}) => cssTheme.layout.spacing[sidePaddings]};
`

const Details = styled.details`
  display: flex;
  flex-direction: column;
  /* ::details-content isn't matched by CssBaseline's box-sizing reset, so restore border-box inheritance */
  &::details-content {
    box-sizing: inherit;
  }
  &.--bottom-aligned {
    flex-direction: column-reverse;
  }
`

export const Header = styled.summary`
  display: flex;
  text-align: left;
  align-items: flex-start;
  margin: 0;
  cursor: pointer;
  gap: ${cssTheme.layout.spacing.s300};
  border-radius: ${cssTheme.layout.radius.lg};
  list-style: none;
  &::-webkit-details-marker {
    display: none;
  }
  &:focus-visible {
    outline: 2px solid ${cssTheme.colors.border.interactive.primaryActive};
  }

  &.--size-sm {
    padding: ${cssTheme.layout.spacing.s300} 0;
    align-self: flex-start;
  }

  &.--size-md {
    padding: ${cssTheme.layout.spacing.s500} 0;
  }

  &.--size-lg {
    padding: ${cssTheme.layout.spacing.s600} 0;
  }
`

export const Content = styled.div`
  display: block;
  ${cssTheme.typography.bodyM}
  &.--with-divider {
    &.--size-sm {
      padding-bottom: ${cssTheme.layout.spacing.s300};
    }

    &.--size-md {
      padding-bottom: ${cssTheme.layout.spacing.s500};
    }

    &.--size-lg {
      padding-bottom: ${cssTheme.layout.spacing.s600};
    }
  }
`

export const HeaderText = styled.span`
  &.--size-sm {
    ${cssTheme.typography.bodyS}
  }
  &.--size-md {
    ${cssTheme.typography.titleM}
  }
  &.--size-lg {
    ${cssTheme.typography.titleL}
  }
  &.--size-md,
  &.--size-lg {
    flex-grow: 1;
  }
`

const IconWrapper = styled.div<{height: number}>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${({height}) => height}px;
`

export const ArrowElementWrapper = styled.div`
  transition: transform 0.2s ease 0s;
  transform: rotateZ(180deg);
  color: ${cssTheme.colors.content.interactive.primary};
  &.--open {
    transform: rotateZ(0);
  }
`

export const Accordion = ({
  header = '',
  isOpenByDefault = false,
  children,
  className,
  onToggle,
  onClick,
  isBottomAligned = false,
  size = 'md',
  icon,
  dataId,
  withDivider = false,
  sidePaddings = 's000'
}: AccordionProps) => {
  const [isOpen, setIsOpen] = useState(isOpenByDefault)

  const handleNativeToggle = (e: React.SyntheticEvent<HTMLDetailsElement>) => {
    const nextIsOpen = e.currentTarget.open
    if (nextIsOpen === isOpen) return
    setIsOpen(nextIsOpen)
    if (onToggle) onToggle(nextIsOpen)
  }

  return (
    <>
      <TabElement
        data-id={dataId}
        sidePaddings={sidePaddings}
        className={className}
      >
        <Details
          open={isOpen}
          onToggle={handleNativeToggle}
          className={cx(isBottomAligned && '--bottom-aligned')}
        >
          <Header className={`--size-${size}`} onClick={onClick}>
            {icon && (
              <IconWrapper height={ICON_WRAPPER_HEIGHT[size]}>
                <Icon
                  name={icon}
                  size={size === 'lg' ? 'lg' : 'md'}
                  colorPath="content.neutral.secondary"
                />
              </IconWrapper>
            )}
            <HeaderText className={`--size-${size}`}>{header}</HeaderText>
            <IconWrapper height={ICON_WRAPPER_HEIGHT[size]}>
              <ArrowElementWrapper
                className={cx(isOpen && '--open', `--size-${size}`)}
              >
                <Icon
                  name="ChevronUp"
                  colorPath="content.neutral.secondary"
                  size="md"
                />
              </ArrowElementWrapper>
            </IconWrapper>
          </Header>
          <Content
            className={cx(`--size-${size}`, withDivider && '--with-divider')}
          >
            {children}
          </Content>
        </Details>
        {withDivider && <Divider />}
      </TabElement>
    </>
  )
}
